import React, { useState, useEffect } from 'react';
import {
    Steps,
    Button,
    Form,
    Input,
    DatePicker,
    Select,
    Switch,
    Space,
    Tag,
    Card,
    Row,
    Col,
    Divider,
    InputNumber,
    Descriptions,
    Typography,
    Collapse,
    Table
} from 'antd';
import SDIModal from "../../../../components/ui/SDIModal";
import {
    CarOutlined,
    UserOutlined,
    FileTextOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { isAfterToday } from '../../../../variables/datesFunctions';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { useNavigate } from 'react-router-dom';

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const Renouvellement = (props) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [modalRenouvellement, setModalRenouvellement] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [contrat, setContrat] = useState();
    const [dateEcheance, setDateEcheance] = useState("");
    const [dateEffet, setDateEffet] = useState("");
    const [fractionnements, setFractionnements] = useState([]);
    const [fractionnement, setFractionnement] = useState("");
    const [selectedFractionnement, setSelectedFractionnement] = useState("");
    const [plaquedVehicule, setPlaquedVehicule] = useState(true);
    const [professions, setProfessions] = useState([]);
    const [marques, setMarques] = useState([]);
    const [preview, setPreview] = useState("");
    const [contratToUpdate, setContratToUpdate] = useState({});
    const [supplementaryGaranties, setSupplementaryGaranties] = useState({});

    // Forms for each step
    const [vehiclesForm] = Form.useForm();
    const [insuredForm] = Form.useForm();
    const [renewalForm] = Form.useForm();

    // Enhanced state for vehicles and insured data
    const [vehicles, setVehicles] = useState([]);
    const [insuredData, setInsuredData] = useState({});
    const [originalVehicles, setOriginalVehicles] = useState([]);
    const [originalInsuredData, setOriginalInsuredData] = useState({});
    const [loadingPreview, setLoadingPreview] = useState(false);

    const getGarantieIdentifier = (garantie) => {
        if (garantie === null || garantie === undefined) {
            return null;
        }
        if (typeof garantie === "string" || typeof garantie === "number") {
            return garantie;
        }
        return (
            garantie?.codeId ??
            garantie?.code ??
            garantie?.garantieId ??
            garantie?.id ??
            garantie?.sousGarantieId ??
            garantie?.sousGarantie?.codeId ??
            garantie?.sousGarantie?.id ??
            null
        );
    };

    const getGarantieLabel = (garantie, fallback) => {
        if (garantie === null || garantie === undefined) {
            return fallback ?? "";
        }
        if (typeof garantie === "string" || typeof garantie === "number") {
            return String(garantie);
        }
        return (
            garantie?.nom ??
            garantie?.libelle ??
            garantie?.designation ??
            garantie?.sousGarantie?.nom ??
            garantie?.garantie?.nom ??
            fallback ??
            String(getGarantieIdentifier(garantie) ?? "")
        );
    };

    const extractSupplementarySelections = (engin) => {
        if (!engin) {
            return [];
        }
        const explicitSelections = Array.isArray(engin.garantiesSupplementaires)
            ? engin.garantiesSupplementaires
            : [];

        const explicitValues = explicitSelections
            .map((gar) => getGarantieIdentifier(gar))
            .filter(Boolean);

        if (explicitValues.length > 0) {
            return explicitValues;
        }

        const optionalFromCompulsory = Array.isArray(engin.compulsoryGuaranties)
            ? engin.compulsoryGuaranties.filter((gar) => !gar?.obligatoire)
            : [];

        if (optionalFromCompulsory.length > 0) {
            return optionalFromCompulsory
                .map((gar) => getGarantieIdentifier(gar))
                .filter(Boolean);
        }

        const optionalFromSousGaranties = Array.isArray(engin.sousGaranties)
            ? engin.sousGaranties.filter((gar) => !gar?.obligatoire)
            : [];

        return optionalFromSousGaranties
            .map((gar) => getGarantieIdentifier(gar))
            .filter(Boolean);
    };

    const getAllSupplementaryDetails = (engin) => {
        if (!engin) {
            return [];
        }
        const collected = new Map();
        const pushGarantie = (gar) => {
            const id = getGarantieIdentifier(gar);
            if (!id) {
                return;
            }
            const key = String(id);
            if (collected.has(key)) {
                return;
            }
            if (typeof gar === "object" && gar !== null) {
                collected.set(key, {
                    ...gar,
                    codeId: gar?.codeId ?? gar?.code ?? key,
                    nom: getGarantieLabel(gar, key)
                });
            } else {
                collected.set(key, {
                    codeId: id,
                    nom: getGarantieLabel(gar, key)
                });
            }
        };

        const lists = [
            ...(Array.isArray(engin.garantiesSupplementaires) ? engin.garantiesSupplementaires : []),
            ...(Array.isArray(engin.compulsoryGuaranties)
                ? engin.compulsoryGuaranties.filter((gar) => !gar?.obligatoire)
                : []),
            ...(Array.isArray(engin.sousGaranties)
                ? engin.sousGaranties.filter((gar) => !gar?.obligatoire)
                : [])
        ];

        lists.forEach(pushGarantie);
        return Array.from(collected.values());
    };

    const navigate = useNavigate();

    useEffect(() => {
        setModalRenouvellement(props.open);
        setContrat(props.contrat);

        // Initialize vehicles and insured data when modal opens
        if (props.contrat && props.open) {
            initializeData(props.contrat);
        }
        if (props?.contrat?.codeId) {
            axiosInstance
                .get(
                    `/api/fractionnement/contrat/codeId/${props?.contrat?.codeId}`)
                .then((res) => {
                    // console.log(res.data);
                    setFractionnements(res.data);
                })
                .catch((err) => {
                    console.log(err);
                });
        }

        axiosInstance
            .get(`/api/parametre/type/PROFESSION`)
            .then((res) => {
                const sorted = [...res.data].sort((a, b) =>
                    (a.libelleParametre || "").localeCompare(
                        b.libelleParametre || "",
                        "fr",
                        { sensitivity: "base" }
                    )
                );
                setProfessions(sorted);
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance
            .get(`/api/parametre/type/MARQUE`)
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                const contratCategorieCode = props?.contrat?.engins?.[0]?.categorie?.codeCategorie?.trim();
                const normalized = contratCategorieCode
                    ? data.filter(
                        (el) =>
                            el?.categorie?.codeCategorie?.trim() === contratCategorieCode
                    )
                    : data;
                const sorted = [...normalized].sort((a, b) =>
                    (a.libelleParametre || "").localeCompare(
                        b.libelleParametre || "",
                        "fr",
                        { sensitivity: "base" }
                    )
                );
                setMarques(sorted);
            })
            .catch((err) => {
                console.log(err);
            });
    }, [props.open, props?.contrat]);

    useEffect(() => {
        if (props?.contrat?.codeId) {
            setSupplementaryGaranties({});
        }
    }, [props?.contrat?.codeId]);

    useEffect(() => {
        const fetchSupplementaryGaranties = async () => {
            if (!props.open || !props?.contrat?.engins?.length || !props?.contrat?.organisme?.code) {
                return;
            }
            const uniqueCategories = [
                ...new Set(
                    props.contrat.engins
                        .map((engin) => engin?.categorie?.categorieId)
                        .filter(Boolean)
                )
            ];
            const categoriesToLoad = uniqueCategories.filter((categorieId) => !supplementaryGaranties[categorieId]);
            if (categoriesToLoad.length === 0) {
                return;
            }
            try {
                const results = await Promise.all(
                    categoriesToLoad.map(async (categorieId) => {
                        try {
                            const response = await axiosInstance.get(
                                `/api/garantie/categorie/non-compulsory/${categorieId}/${props?.contrat?.organisme?.code}`
                            );
                            return { categorieId, garanties: response.data || [] };
                        } catch (error) {
                            console.log(error);
                            return { categorieId, garanties: [] };
                        }
                    })
                );
                if (results.length > 0) {
                    setSupplementaryGaranties((prev) => {
                        const updated = { ...prev };
                        results.forEach(({ categorieId, garanties }) => {
                            updated[categorieId] = garanties;
                        });
                        return updated;
                    });
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchSupplementaryGaranties();
    }, [props.open, props?.contrat, supplementaryGaranties]);

    const initializeData = (contractData) => {
        // Initialize vehicles data
        if (contractData.engins && contractData.engins.length > 0) {
            const vehicleData = contractData.engins.map((engin, index) => {
                const supplementarySelections = extractSupplementarySelections(engin);
                return {
                    id: engin.codeId || index,
                    marque: engin.marque || '',
                    immatriculation: engin.immatriculation || '',
                    numeroChassis: engin.numeroChassis || '',
                    valeurDeclaree: (engin.valeurDeclaree ? engin.valeurDeclaree : contrat?.engins[0]?.categorie?.valeurVenaleMinimale) || 0,
                    valeurVenale: (engin.valeurVenale ? engin.valeurVenale : contrat?.engins[0]?.categorie?.valeurVenaleMinimale) || 0,
                    puissance: engin.puissance || '',
                    energie: engin.energie || '',
                    nombrePlace: engin.nombrePlace || 0,
                    dateMiseEnCirculation: engin.dateMiseEnCirculation ? dayjs(engin.dateMiseEnCirculation) : null,
                    usage: engin.usage || '',
                    carosserie: engin.carosserie || '',
                    categorie: engin.categorie || null,
                    garantiesSupplementaires: supplementarySelections
                };
            });
            setVehicles(vehicleData);
            setOriginalVehicles(vehicleData.map((vehicle) => ({
                ...vehicle,
                garantiesSupplementaires: [...(vehicle.garantiesSupplementaires || [])],
                dateMiseEnCirculation: vehicle.dateMiseEnCirculation?.clone
                    ? vehicle.dateMiseEnCirculation.clone()
                    : vehicle.dateMiseEnCirculation
            })));
        }

        // Initialize insured data
        if (contractData.souscripteur) {
            const insuredInfo = {
                typePersonne: contractData.souscripteur.typePersonne || 'PHYSIQUE',
                nif: contractData.souscripteur.nif || '',
                numeroRegistreCommerce: contractData.souscripteur.numeroRegistreCommerce || '',
                nom: contractData.souscripteur.nom || '',
                prenom: contractData.souscripteur.prenom || '',
                telephone: contractData.souscripteur.contact[0] || '',
                genre: contractData.souscripteur.genre || '',
                email: contractData.souscripteur.email || '',
                adresse: contractData.souscripteur.adresse || '',
                profession: contractData.souscripteur.profession || '',
                dateNaissance: contractData.souscripteur.dateNaissance ? dayjs(contractData.souscripteur.dateNaissance) : null,
                cni: contractData.souscripteur.cni || '',
                permisConduire: contractData.souscripteur.permisConduire || ''
            };
            setInsuredData(insuredInfo);
            setOriginalInsuredData({ ...insuredInfo });
        }
    };

    const resolveSupplementaryGaranties = (selection, originalEngin, categorieId) => {
        if (!Array.isArray(selection)) {
            return [];
        }
        const availableGaranties = categorieId ? supplementaryGaranties?.[categorieId] || [] : [];
        const originalSupplementary = getAllSupplementaryDetails(originalEngin);
        return selection
            .map((selected) => {
                if (selected === null || selected === undefined) {
                    return null;
                }
                if (typeof selected === 'object') {
                    if (selected.codeId || selected.code) {
                        return { ...selected };
                    }
                    const inferredId = getGarantieIdentifier(selected);
                    return inferredId ? { ...selected, codeId: inferredId } : null;
                }
                const selectedKey = String(selected);
                const fromAvailable = availableGaranties.find(
                    (garantie) => String(getGarantieIdentifier(garantie)) === selectedKey
                );
                if (fromAvailable) {
                    return { ...fromAvailable, codeId: getGarantieIdentifier(fromAvailable) };
                }
                const fromOriginal = originalSupplementary.find(
                    (garantie) => String(getGarantieIdentifier(garantie)) === selectedKey
                );
                if (fromOriginal) {
                    return { ...fromOriginal, codeId: getGarantieIdentifier(fromOriginal) };
                }
                return { codeId: selected };
            })
            .filter(Boolean);
    };

    const buildUpdatedContrat = (fractionnementIdParam) => {
        if (!contrat) {
            return null;
        }
        const updatedEngins = vehicles.map((vehicle, index) => {
            const originalEngin = contrat.engins?.[index] || contrat.engins?.find((e) => e.codeId === vehicle.id);
            const categorieId = originalEngin?.categorie?.categorieId || vehicle?.categorie?.categorieId;
            const supplementary = resolveSupplementaryGaranties(
                vehicle.garantiesSupplementaires,
                originalEngin,
                categorieId
            );
            return {
                ...originalEngin,
                ...vehicle,
                garantiesSupplementaires: supplementary,
                dateMiseEnCirculation: vehicle.dateMiseEnCirculation
                    ? vehicle.dateMiseEnCirculation.toISOString()
                    : null
            };
        });

        const updatedContrat = {
            ...contrat,
            dateEffet: dateEffet || contrat.dateEffet,
            dateEcheance: dateEcheance || contrat.dateEcheance,
            engins: updatedEngins,
            souscripteur: {
                ...contrat.souscripteur,
                ...insuredData,
                genre: insuredData?.typePersonne === "MORALE" ? null : insuredData.genre,
                dateNaissance: insuredData.dateNaissance ? insuredData.dateNaissance.toISOString() : null
            }
        };

        const isVehicleCategory = contrat?.engins?.[0]?.categorie?.codeCategorie?.startsWith("V");
        if (!isVehicleCategory) {
            const targetFractionnementId =
                fractionnementIdParam || fractionnement || selectedFractionnement?.fractionnementId;
            if (targetFractionnementId) {
                const duration = fractionnements?.find(
                    (fract) => fract.fractionnementId === targetFractionnementId
                );
                if (duration) {
                    updatedContrat.fractionnement = { ...duration };
                }
            }
        }

        return updatedContrat;
    };

    const showPreview = async (fractionnementIdParam) => {
        const requiresFractionnement = !contrat?.engins?.[0]?.categorie?.codeCategorie?.startsWith("V");
        const targetFractionnementId =
            fractionnementIdParam || fractionnement || selectedFractionnement?.fractionnementId;
        if (!dateEffet || (requiresFractionnement && !targetFractionnementId)) {
            return;
        }
        try {
            setLoadingPreview(true);
            const updatedContrat = buildUpdatedContrat(targetFractionnementId);
            if (!updatedContrat) {
                setLoadingPreview(false);
                return;
            }
            setContratToUpdate(updatedContrat);
            const endpoint = contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V")
                ? plaquedVehicule
                    ? `/api/contrat/renouvellement-preview/vt/plaqued`
                    : `/api/contrat/renouvellement-preview/vt/non-plaqued`
                : `/api/contrat/renouvellement-preview`;

            console.log("endpoint: ", endpoint);


            console.log("updatedContrat: ", updatedContrat);
            const response = await axiosInstance.put(endpoint, JSON.stringify(updatedContrat));
            setPreview(response.data);
            console.log(response.data);
            // toast("Renouvellement effectué avec succès!");

        } catch (error) {
            console.error(error);
            toast("Erreur lors du preview!");
        } finally {
            setLoadingPreview(false);
        }
    }

    const steps = [
        {
            title: 'Véhicules',
            content: 'Informations des véhicules',
            icon: <CarOutlined />
        },
        {
            title: 'Souscripteur/Assuré',
            content: 'Informations du souscripteur / de l\'assuré',
            icon: <UserOutlined />
        },
        {
            title: 'Renouvellement',
            content: 'Paramètres du renouvellement',
            icon: <FileTextOutlined />
        },
        {
            title: 'Récapitulatif',
            content: 'Vérification finale',
            icon: <CheckCircleOutlined />
        }
    ];

    const recapStepIndex = steps.length - 1;

    const nextStep = async () => {
        try {
            if (currentStep === 0) {
                const vehicleFields = [];
                vehicles.forEach((_, index) => {
                    vehicleFields.push(`marque_${index}`, `immatriculation_${index}`, `numeroChassis_${index}`, `puissance_${index}`);
                });
                await vehiclesForm.validateFields(vehicleFields);
                console.log(vehiclesForm.getFieldsValue());
            } else if (currentStep === 1) {
                // Validate insured form based on person type
                const insuredFields = [];
                if (insuredData.typePersonne === 'PHYSIQUE') {
                    insuredFields.push('nom', 'prenom', 'genre', 'typePersonne', 'telephone', 'adresse');
                } else {
                    insuredFields.push('nom', 'typePersonne', 'telephone', 'adresse');
                }
                await vehiclesForm.validateFields(insuredFields);
                console.log(insuredForm.getFieldsValue());
                console.log("vehiclesForm: ", vehiclesForm.getFieldsValue());
            } else if (currentStep === 2) {
                // Validate renewal form
                const renewalFields = ['dateEffet'];
                if (!contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && !fractionnement) {
                    renewalFields.push('fractionnement');
                }
                await vehiclesForm.validateFields(renewalFields);
                console.log(vehiclesForm.getFieldsValue());
            }
            if (currentStep === recapStepIndex - 1) {
                showPreview();
            }
            setCurrentStep(currentStep + 1);
        } catch (error) {
            console.log('Validation failed:', error);
            // Show validation error message
            if (error.errorFields && error.errorFields.length > 0) {
                const firstError = error.errorFields[0];
                toast(`Erreur de validation: ${firstError.errors[0]}`, { type: 'error' });
            }
        }
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleVehicleChange = (index, field, value) => {
        const newVehicles = [...vehicles];
        const updatedVehicle = { ...newVehicles[index] };
        if ((field === "numeroChassis" || field === "immatriculation") && typeof value === "string") {
            value = value.toUpperCase();
        }
        if (field === "garantiesSupplementaires") {
            updatedVehicle[field] = Array.isArray(value) ? value : [];
        } else {
            updatedVehicle[field] = value;
        }
        newVehicles[index] = updatedVehicle;
        setVehicles(newVehicles);
    };

    const handleInsuredChange = (field, value) => {
        if (field === "telephone") {
            const digitsOnly = value.replace(/\D/g, "").slice(0, 8);
            let contacts = [digitsOnly];
            setInsuredData({ ...insuredData, contact: contacts });
        } else if (field === "nom") {
            setInsuredData({ ...insuredData, [field]: value.toUpperCase() });
        } else if (field === "typePersonne" && value === "MORALE") {
            setInsuredData({ ...insuredData, ["genre"]: null });
            setInsuredData({
                ...insuredData,
                [field]: value
            });
        } else {
            setInsuredData({
                ...insuredData,
                [field]: value
            });
        }
    };

    const handleRenouvellementSubmit = async () => {
        setLoading(true);

        try {
            const updatedContrat = buildUpdatedContrat();
            if (!updatedContrat) {
                toast("Impossible de constituer les données du renouvellement.", { type: "error" });
                setLoading(false);
                return;
            }
            setContratToUpdate(updatedContrat);
            const endpoint = contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V")
                ? plaquedVehicule
                    ? `/api/contrat/renouvellement/vt/plaqued`
                    : `/api/contrat/renouvellement/vt/non-plaqued`
                : `/api/contrat/renouvellement`;

            const response = await axiosInstance.put(endpoint, JSON.stringify(updatedContrat));
            if (response.status === 200) {
                toast("Renouvellement effectué avec succès!");
                setLoading(false);
                setModalRenouvellement(false);
                navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
                props.update(false);
                // Reset to first step for next time
                setCurrentStep(0);
            }


        } catch (error) {
            console.error(error);
            toast("Erreur lors du renouvellement!");
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setModalRenouvellement(false);
        setCurrentStep(0);
        props.update(false);
    };

    const getChangedFields = () => {
        const changes = {
            vehicles: [],
            insured: {}
        };

        // Check vehicle changes
        vehicles.forEach((vehicle, index) => {
            const original = originalVehicles[index];
            const vehicleChanges = {};
            Object.keys(vehicle).forEach(key => {
                if (JSON.stringify(vehicle[key]) !== JSON.stringify(original?.[key])) {
                    vehicleChanges[key] = {
                        old: original?.[key],
                        new: vehicle[key]
                    };
                }
            });
            if (Object.keys(vehicleChanges).length > 0) {
                changes.vehicles.push({
                    index,
                    immatriculation: vehicle.immatriculation,
                    changes: vehicleChanges
                });
            }
        });

        // Check insured changes
        Object.keys(insuredData).forEach(key => {
            if (JSON.stringify(insuredData[key]) !== JSON.stringify(originalInsuredData[key])) {
                changes.insured[key] = {
                    old: originalInsuredData[key],
                    new: insuredData[key]
                };
            }
        });

        return changes;
    };
    const disabledDate = (current) => current && isAfterToday(contrat?.dateEcheance) ? current < dayjs(contrat?.dateEcheance) : current < dayjs().add(-1, "day");


    const renderVehiclesStep = () => (
        <Form form={vehiclesForm} layout="vertical">
            <Title level={4}>Informations {vehicles.length === 1 ? 'sur l\'engin' : 'des engins'}</Title>
            {vehicles.map((vehicle, index) => {
                const originalEngin = contrat?.engins?.[index];
                const categorieId = originalEngin?.categorie?.categorieId || vehicle?.categorie?.categorieId;
                const availableSupplementary = supplementaryGaranties?.[categorieId] || [];
                const existingSupplementary = getAllSupplementaryDetails(originalEngin);
                const optionsMap = new Map();
                [...availableSupplementary, ...existingSupplementary].forEach((garantie) => {
                    if (!garantie) {
                        return;
                    }
                    const optionValue = getGarantieIdentifier(garantie);
                    if (optionValue === undefined || optionValue === null) {
                        return;
                    }
                    const key = String(optionValue);
                    if (!optionsMap.has(key)) {
                        optionsMap.set(key, {
                            label: getGarantieLabel(garantie, key),
                            value: optionValue
                        });
                    }
                });
                const garantiesOptions = Array.from(optionsMap.values());

                return (
                    <Card key={index} title={vehicles.length === 1 ? "Caractéristiques de l'engin" : `Engin ${index + 1}`} style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Marque"
                                    name={`marque_${index}`}
                                    initialValue={vehicle.marque}
                                    rules={[{ required: true, message: 'Veuillez saisir la marque' }]}
                                >
                                    {/* <Input
                                    value={vehicle.marque}
                                    onChange={(e) => handleVehicleChange(index, 'marque', e.target.value)}
                                /> */}
                                    <Select
                                        style={{ width: '100%' }}
                                        showSearch
                                        allowClear
                                        placeholder="Sélectionner la marque"
                                        optionFilterProp="label"
                                        value={vehicle.marque || undefined}
                                        onChange={(value) => handleVehicleChange(index, "marque", value || "")}
                                        filterOption={(input, option) =>
                                            (option?.label || "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                        options={marques?.map((mar, id) => ({
                                            label: mar.libelleParametre,
                                            value: mar.libelleParametre,
                                            key: id
                                        })) || []}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Immatriculation"
                                    name={`immatriculation_${index}`}
                                    initialValue={vehicle.immatriculation}
                                    rules={[{ required: true, message: 'Veuillez saisir l\'immatriculation' }]}
                                >
                                    <Input
                                        value={vehicle.immatriculation}
                                        style={{ textTransform: 'uppercase' }}
                                        onChange={(e) => {
                                            const formatted = e.target.value
                                                .toUpperCase()                // force uppercase
                                                .replace(/[^A-Z0-9]/g, "");   // allow only letters and numbers
                                            handleVehicleChange(index, 'immatriculation', formatted);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Numéro de Châssis"
                                    name={`numeroChassis_${index}`}
                                    initialValue={vehicle.numeroChassis}
                                    rules={[{ required: true, message: 'Veuillez saisir le numero de châssis' }, { min: 10, message: 'Le numéro de châssis doit avoir 10 caracteres au minimum sinon 17' }]}
                                >
                                    <Input
                                        value={vehicle.numeroChassis}
                                        style={{ textTransform: 'uppercase' }}
                                        onChange={(e) => {
                                            const formatted = e.target.value
                                                .toUpperCase()                // force uppercase
                                                .replace(/[^A-Z0-9]/g, "");   // allow only letters and numbers
                                            handleVehicleChange(index, 'numeroChassis', formatted);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Puissance"
                                    name={`puissance_${index}`}
                                    initialValue={vehicle.puissance}
                                    rules={[{ required: true, message: 'Veuillez saisir la puissance' }]}
                                >
                                    <InputNumber
                                        value={vehicle.puissance}
                                        onChange={(value) => handleVehicleChange(index, 'puissance', value)}
                                        formatter={(value) =>
                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                        }
                                        parser={(value) =>
                                            value.replace(/\s/g, "")
                                        }
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Valeur Déclarée"
                                    name={`valeurDeclaree_${index}`}
                                    initialValue={vehicle.valeurDeclaree}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={vehicle.valeurDeclaree}
                                        formatter={(value) =>
                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                        }
                                        parser={(value) =>
                                            value.replace(/\s/g, "")
                                        }
                                        onChange={(value) => handleVehicleChange(index, 'valeurDeclaree', value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Valeur Vénale"
                                    name={`valeurVenale_${index}`}
                                    initialValue={vehicle.valeurVenale}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={vehicle.valeurVenale}
                                        formatter={(value) =>
                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                        }
                                        parser={(value) =>
                                            value.replace(/\s/g, "")
                                        }
                                        onChange={(value) => handleVehicleChange(index, 'valeurVenale', value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Nombre de Places"
                                    name={`nombrePlace_${index}`}
                                    initialValue={vehicle.nombrePlace}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={vehicle.nombrePlace}
                                        onChange={(value) => handleVehicleChange(index, 'nombrePlace', value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item label="Garanties supplémentaires">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        value={vehicle.garantiesSupplementaires}
                                        options={garantiesOptions}
                                        onChange={(value) => handleVehicleChange(index, 'garantiesSupplementaires', value)}
                                        placeholder="Sélectionner les garanties supplémentaires"
                                        optionFilterProp="label"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                );
            })}
        </Form>
    );

    const renderInsuredStep = () => (
        <Form form={insuredForm} layout="vertical">
            <Title level={4}>Informations du souscripteur/assuré</Title>
            <Card>
                {/* Type de personne selector */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Form.Item
                            label="Type de personne"
                            name="typePersonne"
                            initialValue={insuredData.typePersonne}
                            rules={[{ required: true, message: 'Veuillez sélectionner le type de personne' }]}
                        >
                            <Select
                                value={insuredData.typePersonne}
                                onChange={(value) => handleInsuredChange('typePersonne', value)}
                                style={{ width: '200px' }}
                            >
                                <Option value="PHYSIQUE">Personne Physique</Option>
                                <Option value="MORALE">Personne Morale</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider />

                {insuredData.typePersonne === 'PHYSIQUE' ? (
                    // Physical Person Fields
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Nom"
                                    name="nom"
                                    initialValue={insuredData.nom}
                                    rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
                                >
                                    <Input
                                        value={insuredData.nom}
                                        onChange={(e) => handleInsuredChange('nom', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Prénom(s)"
                                    name="prenom"
                                    initialValue={insuredData.prenom}
                                    rules={[{ required: true, message: 'Veuillez saisir les prénoms' }]}
                                >
                                    <Input
                                        value={insuredData.prenom}
                                        onChange={(e) => handleInsuredChange('prenom', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Date de Naissance"
                                    name="dateNaissance"
                                    initialValue={insuredData.dateNaissance}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={insuredData.dateNaissance}
                                        onChange={(date) => handleInsuredChange('dateNaissance', date)}
                                        placeholder="Date de naissance"
                                        disabledDate={current => current && current > dayjs().add(-5, 'year')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Lieu de Naissance"
                                    name="lieuNaissance"
                                    initialValue={insuredData.lieuNaissance}
                                >
                                    <Input
                                        value={insuredData.lieuNaissance}
                                        onChange={(e) => handleInsuredChange('lieuNaissance', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Genre"
                                    name="genre"
                                    initialValue={insuredData.genre}
                                    rules={[{ required: true, message: 'Veuillez choisir le genre' }]}
                                >
                                    <Select
                                        value={insuredData.genre}
                                        onChange={(value) => handleInsuredChange('genre', value)}
                                        style={{ width: '100%' }}
                                        placeholder="Sélectionner"
                                    >
                                        <Option value="MASCULIN">Masculin</Option>
                                        <Option value="FEMININ">Féminin</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="CNI/Passeport"
                                    name="cni"
                                    initialValue={insuredData.cni}
                                >
                                    <Input
                                        value={insuredData.cni}
                                        onChange={(e) => handleInsuredChange('cni', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Profession"
                                    name="profession"
                                    initialValue={insuredData.profession}
                                >
                                    {/* <Input
                                        value={insuredData.profession}
                                        onChange={(e) => handleInsuredChange('profession', e.target.value)}
                                    />
                                    <label for="profession" class="form-label">
                                        Profession
                                    </label> */}
                                    <Select
                                        id="profession"
                                        className="w-100"
                                        showSearch
                                        placeholder="Selectionnez la profession"
                                        optionFilterProp="children"
                                        value={insuredData.profession}
                                        onChange={(value) => handleInsuredChange('profession', value)}
                                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        style={{ width: '100%' }}
                                    >
                                        {professions && professions?.map((prof, id) => (<Option key={id}
                                            value={prof.libelleParametre}>
                                            {prof.libelleParametre}
                                        </Option>))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                ) : (
                    // Moral Person Fields
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Dénomination Sociale"
                                    name="nom"
                                    initialValue={insuredData.nom}
                                    rules={insuredData.typePersonne === 'MORALE' ? [{ required: true, message: 'Veuillez saisir la dénomination sociale' }] : []}
                                >
                                    <Input
                                        value={insuredData.nom}
                                        onChange={(e) => handleInsuredChange('nom', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Numéro Registre de Commerce"
                                    name="numeroRegistreCommerce"
                                    initialValue={insuredData.numeroRegistreCommerce}
                                >
                                    <Input
                                        value={insuredData.numeroRegistreCommerce}
                                        onChange={(e) => handleInsuredChange('numeroRegistreCommerce', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="NIF"
                                    name="nif"
                                    initialValue={insuredData.nif}
                                >
                                    <Input
                                        value={insuredData.nif}
                                        onChange={(e) => handleInsuredChange('nif', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                {/* Common fields for both types */}
                <Divider orientation="left">Informations de Contact</Divider>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Téléphone"
                            name="telephone"
                            initialValue={insuredData.telephone}
                            rules={[{ required: true, message: 'Veuillez saisir le téléphone' }]}
                        >
                            <Input
                                value={insuredData.telephone}
                                onChange={(e) => handleInsuredChange('telephone', e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Email"
                            name="email"
                            initialValue={insuredData.email}
                            rules={[{ type: 'email', message: 'Format email invalide' }]}
                        >
                            <Input
                                value={insuredData.email}
                                onChange={(e) => handleInsuredChange('email', e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Adresse"
                            name="adresse"
                            initialValue={insuredData.adresse}
                            rules={[{ required: true, message: 'Veuillez saisir l\'adresse' }]}
                        >
                            <TextArea
                                rows={3}
                                value={insuredData.adresse}
                                onChange={(e) => handleInsuredChange('adresse', e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
        </Form>
    );

    const renderRenewalStep = () => (
        <Form form={renewalForm} layout="vertical">
            <Title level={4}>Paramètres du renouvellement</Title>
            <Card>
                <Row gutter={16}>
                    <Col span={24}>
                        <Text>Date d'échéance du contrat actuel: </Text>
                        <Tag color="blue">
                            <b>{contrat?.dateEcheance ? dayjs(contrat.dateEcheance).format('DD/MM/YYYY HH:mm') : ''}</b>
                        </Tag>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Nouvelle Date d'Effet"
                            name="dateEffet"
                            rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                        >
                            <DatePicker
                                showTime
                                value={dateEffet ? dayjs(dateEffet) : null}
                                onChange={(selectedDate) => {
                                    setDateEffet(selectedDate.toISOString());
                                    if (!contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && fractionnement) {
                                        const { moisMin } = JSON.parse(fractionnement);
                                        if (moisMin > 1) {
                                            const newEcheance = selectedDate.clone().add(moisMin, "M").subtract(1, "s");
                                            setDateEcheance(newEcheance.toISOString());
                                        }
                                    } else {
                                        const newEcheance = selectedDate.clone().add(15, "d").subtract(1, "s");
                                        setDateEcheance(newEcheance.toISOString());
                                    }
                                }}
                                style={{ width: '100%' }}
                                disabledDate={disabledDate}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        {!contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && (
                            <Form.Item
                                label="Durée"
                                name="fractionnement"
                                rules={[{ required: true, message: 'Veuillez sélectionner une durée' }]}
                            >
                                <Select
                                    value={fractionnement}
                                    onChange={(value) => {
                                        console.log("value: ", value);
                                        setFractionnement(value);
                                        let fr = fractionnements.find(fract => fract.fractionnementId === value);
                                        sessionStorage.setItem("fractionnement", JSON.stringify(fr));
                                        console.log("fr: ", fr);
                                        setSelectedFractionnement(fr || null);
                                        if (dateEffet) {
                                            const date = dayjs(dateEffet);
                                            const newEcheance = date.add(fr.moisMin === 1 ? 12 : fr.moisMin, 'M');
                                            setDateEcheance(newEcheance.toISOString());
                                            showPreview(value);
                                        }
                                    }}
                                    style={{ width: '100%' }}
                                >
                                    {fractionnements?.map((fract, index) => (
                                        <Option key={index} value={fract.fractionnementId}>
                                            {fract.moisMin === 1 ? `${fract.moisMin} An` : `${fract.moisMin} Mois`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Col>
                </Row>

                {dateEcheance && (
                    <Row>
                        <Col span={24}>
                            <Text>Nouvelle Date d'Échéance: </Text>
                            <Tag color="green">
                                <b>{dayjs(dateEcheance).format('DD/MM/YYYY HH:mm')}</b>
                            </Tag>
                        </Col>
                    </Row>
                )}

                {contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && (
                    <Row style={{ marginTop: 16 }}>
                        <Col span={24}>
                            <Form.Item label="Type de véhicule">
                                <Switch
                                    checkedChildren="Plaqué"
                                    unCheckedChildren="Non plaqué"
                                    checked={plaquedVehicule}
                                    onChange={(checked) => setPlaquedVehicule(checked)}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Card>
        </Form>
    );

    const renderRecapStep = () => {
        const changes = getChangedFields();

        return (
            <div>
                <Title level={4}>Récapitulatif du renouvellement</Title>

                {/* Contract renewal info */}
                <Card title="Informations du renouvellement" style={{ marginBottom: 16 }}>
                    <Descriptions column={2}>
                        <Descriptions.Item label="Ancienne échéance">
                            {contrat?.dateEcheance ? dayjs(contrat.dateEcheance).format('DD/MM/YYYY HH:mm') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Nouvelle date d'effet">
                            {dateEffet ? dayjs(dateEffet).format('DD/MM/YYYY HH:mm') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Nouvelle échéance">
                            {preview ? dayjs(preview.dateEcheance).format('DD/MM/YYYY HH:mm') : dayjs(dateEcheance).format('DD/MM/YYYY HH:mm')}
                        </Descriptions.Item>
                        {contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && (
                            <Descriptions.Item label="Type véhicule">
                                <Tag color={plaquedVehicule ? "green" : "orange"}>
                                    {plaquedVehicule ? "Plaqué" : "Non plaqué"}
                                </Tag>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </Card>
                {/* Financial Preview */}
                <Card
                    title="Détails Financiers du Renouvellement"
                    style={{ marginBottom: 16 }}
                    loading={loadingPreview}
                >
                    {preview ? (
                        <div>
                            {/* Prime Summary */}
                            <Row gutter={16} style={{ marginBottom: 20 }}>
                                <Col span={24}>
                                    <div style={{
                                        backgroundColor: '#f6ffed',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: 6,
                                        padding: 16,
                                        textAlign: 'center'
                                    }}>
                                        <Title level={3} style={{ color: '#389e0d', margin: 0 }}>
                                            Prime Totale: {displayNumbers(preview.primeTotale)} F CFA
                                        </Title>
                                    </div>
                                </Col>
                            </Row>

                            {/* Detailed breakdown */}
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Card size="small" title="Détail de la Prime">
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="Prime Nette">
                                                <Text strong>{displayNumbers(preview.primeNette)} F CFA</Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Accessoires">
                                                <Text>{displayNumbers(preview.accessoires)} F CFA</Text>
                                            </Descriptions.Item>
                                            {preview.fractionnement && preview.primeNetteFractionnee && (
                                                <>
                                                    <Descriptions.Item label="Prime Nette Fractionnée (+accessoires)">
                                                    </Descriptions.Item>
                                                    <br />
                                                    <div>
                                                        <Text strong style={{ color: '#1890ff', marginLeft: 100 }}>
                                                            {displayNumbers(preview.primeNetteFractionnee)} F CFA
                                                        </Text>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            {/* <Text>Fractionnement: {selectedFractionnement?.nombre}x</Text> */}
                                                            <Text style={{ marginLeft: 8 }}>
                                                                ({((preview.fractionnement)).toFixed(1)}% de la prime annuelle)
                                                            </Text>
                                                        </div>
                                                    </div></>
                                            )}
                                            <Descriptions.Item label="Taxes">
                                                <Text>{displayNumbers(preview.taxes)} F CFA</Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Prime Totale">
                                                <Text strong style={{ fontSize: '16px', color: '#389e0d' }}>
                                                    {displayNumbers(preview.primeTotale)} F CFA
                                                </Text>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card size="small" title="Calculs Bonus/Malus & RC">
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="Coefficient Bonus/Malus">
                                                <Tag color={preview.bonusMalus < 0 ? 'red' : 'green'}>
                                                    {preview.bonusMalus || 0} %
                                                </Tag>
                                                <Text style={{ marginLeft: 8, fontSize: '12px' }}>
                                                    {preview.bonusMalus < 0 ? 'Malus' : 'Bonus'}
                                                </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Réduction Flotte">
                                                <Text style={{ color: preview.flotte > 0 ? '#389e0d' : 'inherit' }}>
                                                    {preview.flotte > 0 ? '-' : ''}{displayNumbers(Math.abs(preview.flotte || 0))} %
                                                </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="RC Défaut">
                                                <Text>{displayNumbers(preview.rcDefaut)} F CFA </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="RC Final">
                                                <Text strong>{displayNumbers(preview.rcFinal)} F CFA</Text>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Compulsory Guaranties */}
                            {preview.compulsoryGuaranties && preview.compulsoryGuaranties.length > 0 && (
                                <div style={{ marginTop: 20 }}>
                                    <Title level={5}>Garanties Obligatoires</Title>
                                    <Table
                                        dataSource={preview.compulsoryGuaranties.filter((g) => g.primeNette > 0 && !g.nom.toUpperCase().startsWith("SURPRIME"))}
                                        pagination={false}
                                        size="small"
                                        columns={[
                                            {
                                                title: 'Garantie',
                                                dataIndex: 'nom',
                                                key: 'nom'
                                            },
                                            {
                                                title: 'Capital',
                                                dataIndex: 'capital',
                                                key: 'capital',
                                                render: (capital) => <Text strong>{displayNumbers(capital)}</Text>
                                            },
                                            {
                                                title: 'Prime Nette',
                                                dataIndex: 'primeNette',
                                                key: 'primeNette',
                                                render: (primeNette, record) => <Text strong>{displayNumbers(record.nom === "RC" ? preview.rcFinal : primeNette)}</Text>
                                            }
                                        ]}
                                    />
                                </div>
                            )}

                            {/* Calculation explanation */}
                            {/* <Card
                                size="small"
                                title="Explication du Calcul"
                                style={{ marginTop: 16, backgroundColor: '#fafafa' }}
                            >
                                <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                    <p><Text strong>Méthode de calcul de la prime de renouvellement :</Text></p>
                                    <ol style={{ paddingLeft: 20 }}>
                                        <li>
                                            <Text strong>Prime de base :</Text> Calcul selon les garanties sélectionnées et la valeur du véhicule
                                        </li>
                                        <li>
                                            <Text strong>Application Bonus/Malus :</Text>
                                            {preview.bonusMalus <= 1
                                                ? ` Réduction de ${((preview.bonusMalus)).toFixed(0)}% (Bonus)`
                                                : ` Majoration de ${((preview.bonusMalus)).toFixed(0)}% (Malus)`
                                            }
                                        </li>
                                        {preview.flotte > 0 && (
                                            <li>
                                                <Text strong>Réduction Flotte :</Text> Remise de {displayNumbers(preview.flotte)} appliquée
                                            </li>
                                        )}
                                        <li>
                                            <Text strong>Ajout des accessoires :</Text> Frais de gestion et commissions ({displayNumbers(preview.accessoires)})
                                        </li>
                                        <li>
                                            <Text strong>Calcul des taxes :</Text> Application du taux de taxe en vigueur ({displayNumbers(preview.taxes)})
                                        </li>
                                    </ol>
                                    <Divider style={{ margin: '12px 0' }} />
                                    <Text strong style={{ color: '#389e0d' }}>
                                        Prime Finale = (Prime Nette × Coeff. Bonus/Malus) - Réduction Flotte + Accessoires + Taxes
                                    </Text>
                                </div>
                            </Card> */}

                            {/* Comparison with previous contract */}
                            {/* {contrat?.primeTotale && (
                                <Card
                                    size="small"
                                    title="Comparaison avec le contrat précédent"
                                    style={{ marginTop: 16 }}
                                >
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Descriptions.Item label="Prime précédente">
                                                <Text>{displayNumbers(contrat.primeTotale)}</Text>
                                            </Descriptions.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Descriptions.Item label="Nouvelle prime">
                                                <Text strong>{displayNumbers(preview.primeTotale)}</Text>
                                            </Descriptions.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Descriptions.Item label="Différence">
                                                <Text
                                                    style={{
                                                        color: preview.primeTotale > contrat.primeTotale ? '#cf1322' : '#389e0d',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {preview.primeTotale > contrat.primeTotale ? '+' : ''}
                                                    {displayNumbers(preview.primeTotale - contrat.primeTotale)}
                                                    {' '}
                                                    ({((preview.primeTotale - contrat.primeTotale) / contrat.primeTotale * 100).toFixed(1)}%)
                                                </Text>
                                            </Descriptions.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            )} */}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                            <Text type="secondary">
                                Les détails financiers seront chargés automatiquement...
                            </Text>
                        </div>
                    )}
                </Card>
                {/* Changes summary */}
                <Collapse>
                    <Panel header="Modifications apportées" key="changes">
                        {changes.vehicles.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Title level={5}>Véhicules modifiés:</Title>
                                {changes.vehicles.map((vehicleChange, index) => (
                                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                                        <Text strong>Véhicule: {vehicleChange.immatriculation}</Text>
                                        <ul style={{ marginTop: 8 }}>
                                            {Object.entries(vehicleChange.changes).map(([field, change]) => (
                                                <li key={field}>
                                                    <Text>{field.toUpperCase()}: </Text>
                                                    <Text delete>{String(change.old)}</Text>
                                                    <Text> → </Text>
                                                    <Text mark>{String(change.new)}</Text>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {Object.keys(changes.insured).length > 0 && (
                            <div>
                                <Title level={5}>Informations assuré modifiées:</Title>
                                <Card size="small">
                                    <ul>
                                        {Object.entries(changes.insured).map(([field, change]) => (
                                            <li key={field}>
                                                <Text>{field.toUpperCase()}: </Text>
                                                <Text delete>{String(change.old)}</Text>
                                                <Text> → </Text>
                                                <Text mark>{String(change.new)}</Text>
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            </div>
                        )}

                        {changes.vehicles.length === 0 && Object.keys(changes.insured).length === 0 && (
                            <Text type="secondary">Aucune modification apportée aux données existantes.</Text>
                        )}
                    </Panel>
                </Collapse>
            </div>
        );
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return renderVehiclesStep();
            case 1:
                return renderInsuredStep();
            case 2:
                return renderRenewalStep();
            case 3:
                return renderRecapStep();
            default:
                return null;
        }
    };

    return (
        <SDIModal
            title="RENOUVELLEMENT DE CONTRAT"
            width={1000}
            open={modalRenouvellement}
            onCancel={handleCancel}
            footer={null}
            zIndex={2000}
            destroyOnClose
        >
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                {steps.map((step, index) => (
                    <Step
                        key={index}
                        title={step.title}
                        icon={step.icon}
                    />
                ))}
            </Steps>

            <div style={{ marginBottom: 24, minHeight: 400 }}>
                {renderStepContent()}
            </div>

            <div style={{ textAlign: 'right' }}>
                <Space>
                    {currentStep > 0 && (
                        <Button onClick={prevStep}>
                            Précédent
                        </Button>
                    )}

                    <Button onClick={handleCancel}>
                        Annuler
                    </Button>

                    {currentStep < steps.length - 1 && (
                        <Button type="primary" onClick={nextStep}>
                            Suivant
                        </Button>
                    )}

                    {currentStep === steps.length - 1 && (
                        <Button
                            type="primary"
                            loading={loading}
                            onClick={handleRenouvellementSubmit}
                        >
                            Confirmer le renouvellement
                        </Button>
                    )}
                </Space>
            </div>
        </SDIModal>
    );
};

Renouvellement.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default Renouvellement;
